import { AlertTriangle, X } from "lucide-react";
import type React from "react";
import { useId } from "react";
import { Button } from "./ui/Button";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | false | Promise<void | false>;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info" | "primary";
  children?: React.ReactNode;
  confirmEnabled?: boolean;
}

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmar ação",
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  variant = "warning",
  children,
  confirmEnabled = true,
}: ConfirmModalProps) {
  const modalTitleId = useId();
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          iconBg: "bg-red-100",
          iconColor: "text-red-600",
          confirmBtn: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
        };
      case "warning":
        return {
          iconBg: "bg-yellow-100",
          iconColor: "text-yellow-600",
          confirmBtn: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
        };
      case "info":
        return {
          iconBg: "bg-blue-100",
          iconColor: "text-blue-600",
          confirmBtn: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
        };
      default:
        return {
          iconBg: "bg-yellow-100",
          iconColor: "text-yellow-600",
          confirmBtn: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={handleBackdropClick}
      role="none"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-11/12  overflow-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${styles.iconBg}`}>
              <AlertTriangle className={`h-5 w-5 ${styles.iconColor}`} />
            </div>
            <h3 id={modalTitleId} className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4">{children ? children : message ? <p className="text-gray-700">{message}</p> : null}</div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            {cancelText}
          </Button>
          <Button
            onClick={() => {
              Promise.resolve(onConfirm()).then((result) => {
                if (result !== false) {
                  onClose();
                }
              });
            }}
            className="flex-1"
            variant={
              variant === "danger"
                ? "destructive"
                : variant === "info"
                  ? "info"
                  : variant === "primary"
                    ? "primary"
                    : "warning"
            }
            disabled={!confirmEnabled}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
